import React, { useEffect, useState } from 'react';
import Layout from '../layout/Layout';
import { termApi } from '../../services/api';
import '../admin/Admin.css';

const getPayload = (response, fallback) => response?.data?.data ?? response?.data ?? fallback;
const asArray = (value) => (Array.isArray(value) ? value : value?.bookings || value?.invitations || value?.items || []);

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : 'TBC');

// Defect D: serializeTeacherBooking (Core termBooking.routes.ts) always
// populates start_date/end_date from the TERM INSTANCE's own dates, so a
// naive fallback chain (start_date || ... || coverage_start_date) never
// reaches coverage_start_date/coverage_end_date — the teacher's own booked
// window (billable_start_date/billable_end_date) — because start_date is
// always truthy. Show the booking's own coverage window whenever it differs
// from the term window, and label which is which so a teacher can tell
// "my classes run X-Y" from "the term runs A-B" instead of always seeing the
// whole term.
export const formatDateRange = (booking) => {
  const termStart = booking.start_date || booking.term_start_date;
  const termEnd = booking.end_date || booking.term_end_date;
  const coverageStart = booking.coverage_start_date;
  const coverageEnd = booking.coverage_end_date;

  const hasTerm = Boolean(termStart || termEnd);
  const hasCoverage = Boolean(coverageStart || coverageEnd);

  if (!hasTerm && !hasCoverage) return 'Dates to be confirmed';

  const termLabel = hasTerm ? `${formatDate(termStart)} - ${formatDate(termEnd)}` : null;
  const coverageLabel = hasCoverage ? `${formatDate(coverageStart)} - ${formatDate(coverageEnd)}` : null;

  if (hasCoverage && hasTerm && coverageLabel !== termLabel) {
    return `Your classes: ${coverageLabel} (term: ${termLabel})`;
  }

  if (hasCoverage && !hasTerm) return `Your classes: ${coverageLabel}`;

  return termLabel;
};

const getInvitationMessage = (booking) => {
  if (booking.expiry_message) return booking.expiry_message;
  if (booking.status === 'cancelled' || booking.invitation_status === 'cancelled') {
    return 'This invitation was cancelled by an admin and can no longer be accepted or rejected.';
  }
  if (booking.status === 'expired' || booking.invitation_status === 'expired') {
    return 'This invitation has expired and can no longer be accepted or rejected.';
  }
  if (booking.term_has_started && booking.status === 'rejected') {
    return 'This term has already started, so further rejection changes are blocked.';
  }
  return '';
};

const canActOnInvitation = (booking) => {
  const status = booking.status || booking.invitation_status;
  return !['accepted', 'admin_confirmed', 'rejected', 'cancelled', 'expired'].includes(status);
};

const TeacherTermBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const loadBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await termApi.listMyTermBookings();
      setBookings(asArray(getPayload(response, [])));
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.code || 'Unable to load term planner.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const runAction = async (id, action, successMessage) => {
    setActionId(id);
    setError('');
    setStatus('');
    try {
      await action();
      setStatus(successMessage);
      await loadBookings();
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.code || 'Unable to update term planner booking.';
      setError(message);
    } finally {
      setActionId('');
    }
  };

  const updateRenewal = (booking, patch) => runAction(
    booking.id,
    () => termApi.updateMyRenewal(booking.id, patch),
    'Renewal preference saved.',
  );

  const acceptInvitation = (booking) => runAction(
    booking.invitation_id || booking.id,
    () => termApi.acceptInvitation(booking.invitation_id || booking.id),
    'Invitation accepted.',
  );

  const rejectInvitation = (booking) => {
    const reason = window.prompt('Reason for rejecting this term planner booking?') || '';
    return runAction(
      booking.invitation_id || booking.id,
      () => termApi.rejectInvitation(booking.invitation_id || booking.id, { reason }),
      'Invitation rejected.',
    );
  };

  return (
    <Layout>
      <div className="teacher-term-page" data-testid="WEB-TERM-013">
        <header className="page-header">
          <div>
            <h1>Term Planner</h1>
            <p className="subtitle">Review invitations and manage future renewal preferences.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={loadBookings}>Refresh</button>
        </header>

        {error && <div className="form-error" role="alert">{error}</div>}
        {status && <div className="form-success" role="status">{status}</div>}

        {loading ? (
          <div className="loading-spinner">Loading term planner...</div>
        ) : bookings.length === 0 ? (
          <div className="empty-state">No term planner bookings yet.</div>
        ) : (
          <div className="term-planner-grid" data-testid="WEB-TERM-008">
            {bookings.map((booking) => {
              const invitationId = booking.invitation_id || booking.id;
              const disabled = actionId === booking.id || actionId === invitationId;
              const canRenew = booking.auto_renew_admin !== false;
              const canAutoAccept = booking.auto_accept_admin !== false;
              const invitationMessage = getInvitationMessage(booking);
              const actionDisabled = disabled || !canActOnInvitation(booking);

              return (
                <article key={booking.id || invitationId} className="term-slot-card" data-testid="WEB-TERM-007">
                  <div className="term-slot-card-header">
                    <div>
                      <h4>{booking.title || booking.term_name || 'Term planner booking'}</h4>
                      <p>{booking.room_name || 'Room to be confirmed'} / {formatDateRange(booking)}</p>
                    </div>
                    <span className={`booking-status ${booking.status || 'pending'}`}>
                      {booking.status || booking.invitation_status || 'pending'}
                    </span>
                  </div>

                  <div className="term-booking-list">
                    <div className="term-booking-row">
                      <div>
                        <strong>{booking.weekday_label || booking.day_label || 'Weekly slot'}</strong>
                        <span>{booking.start_time || 'TBC'} - {booking.end_time || 'TBC'}</span>
                      </div>
                    </div>
                    {booking.source_booking_id && (
                      <div className="term-booking-row" data-testid="WEB-TERM-009">
                        <div>
                          <strong>Renewed from booking #{booking.source_booking_id}</strong>
                          <span>Renewal keeps the previous booking flags unless an admin changes them.</span>
                        </div>
                      </div>
                    )}
                    {booking.sync_status && (
                      <div className="term-booking-row" data-testid="WEB-TERM-011">
                        <div>
                          <strong>Calendar sync</strong>
                          <span>{booking.sync_status}</span>
                        </div>
                      </div>
                    )}
                    {booking.notification_status && (
                      <div className="term-booking-row" data-testid="WEB-TERM-016">
                        <div>
                          <strong>Notification</strong>
                          <span>{booking.notification_status}</span>
                        </div>
                      </div>
                    )}
                    {invitationMessage && (
                      <div className="form-error">{invitationMessage}</div>
                    )}
                  </div>

                  <div className="term-booking-actions">
                    <button type="button" className="btn btn-success" disabled={actionDisabled} onClick={() => acceptInvitation(booking)}>
                      Accept
                    </button>
                    <button type="button" className="btn btn-danger" disabled={actionDisabled || booking.term_has_started} onClick={() => rejectInvitation(booking)}>
                      Reject
                    </button>
                  </div>

                  <div className="term-booking-list">
                    <label className="term-checkbox-row compact">
                      <input
                        type="checkbox"
                        checked={booking.auto_renew_user !== false}
                        disabled={!canRenew || disabled}
                        onChange={(event) => updateRenewal(booking, {
                          auto_renew_user: event.target.checked,
                          auto_accept_user: booking.auto_accept_user !== false,
                        })}
                      />
                      Renew future term when admin renewal is enabled
                    </label>
                    <label className="term-checkbox-row compact">
                      <input
                        type="checkbox"
                        checked={Boolean(booking.auto_accept_user)}
                        disabled={!canAutoAccept || disabled}
                        onChange={(event) => updateRenewal(booking, {
                          auto_renew_user: booking.auto_renew_user !== false,
                          auto_accept_user: event.target.checked,
                        })}
                      />
                      Auto-accept matching renewal when admin allows it
                    </label>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeacherTermBookings;
